import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";

type DataTablePaginationProps = {
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
  canPrevious: boolean;
  canNext: boolean;
};

function DataTablePagination({
  page: pageProp,
  pages,
  onPageChange,
  canPrevious,
  canNext
}: DataTablePaginationProps) {
  const [page, setPage] = useState(pageProp);

  useEffect(() => {
    setPage(pageProp);
  }, [pageProp]);

  const getSafePage = useCallback(
    (nextPage: number) => {
      let p = nextPage;
      if (Number.isNaN(p)) {
        p = pageProp;
      }
      return Math.min(Math.max(p, 0), pages - 1);
    },
    [pageProp, pages]
  );

  const changePage = useCallback(
    (nextPage: number) => {
      const safe = getSafePage(nextPage);
      setPage(safe);
      if (pageProp !== safe) {
        onPageChange(safe);
      }
    },
    [getSafePage, onPageChange, pageProp]
  );

  const pageClick = useCallback(
    (pageIndex: number) => {
      changePage(pageIndex);
    },
    [changePage]
  );

  const pageButtons = useMemo(() => {
    const buttons = [];
    for (let i = 0; i < pages; i += 1) {
      const active = page === i;
      buttons.push(
        <PaginationItem key={i} active={active} className="vsdv">
          <PaginationLink onClick={() => pageClick(i)}>{i + 1}</PaginationLink>
        </PaginationItem>
      );
    }
    return buttons;
  }, [page, pages, pageClick]);

  return (
    <Pagination
      size="sm"
      listClassName="justify-content-center"
      aria-label="Page navigation example"
    >
      <PaginationItem className={`${!canPrevious && "disabled"}`}>
        <PaginationLink
          className="prev"
          onClick={() => {
            if (!canPrevious) return;
            changePage(page - 1);
          }}
          disabled={!canPrevious}
        >
          <i className="simple-icon-arrow-left" />
        </PaginationLink>
      </PaginationItem>

      {pageButtons}
      <PaginationItem className={`${!canNext && "disabled"}`}>
        <PaginationLink
          className="next"
          onClick={() => {
            if (!canNext) return;
            changePage(page + 1);
          }}
          disabled={!canNext}
        >
          <i className="simple-icon-arrow-right" />
        </PaginationLink>
      </PaginationItem>
    </Pagination>
  );
}

export default DataTablePagination;
